import React from "react";

type IconProps = {
  children: React.ReactNode;
  className: string;
  clipPath: string;
  transform: string;
};

type TileProps = {
  title: string;
  titleX: number;
  titleY: number;
  titleTilt?: number;
  labelWidth: number;
  clipPath: string;
  iconClipPath: string;
  leftTransform: string;
  rightTransform: string;
  topTransform: string;
  frameTransform: string;
  iconTransform: string;
  topPath: string;
  hoverColor: string;
  isActive: boolean;
  children: React.ReactNode;
};

const IconGlyph = ({ children, className, clipPath, transform }: IconProps) => (
  <g clipPath={`url(#${clipPath})`}>
    <g
      transform={transform}
      className={`text-[var(--tile-line)] ${className}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.65"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </g>
  </g>
);

const getTitleLines = (title: string) => {
  const words = title.trim().split(/\s+/);

  if (title.length <= 18 || words.length < 3) {
    return [title];
  }

  const halfLength = title.length / 2;
  let splitIndex = 1;
  let currentLength = 0;

  words.forEach((word, index) => {
    currentLength += word.length + (index > 0 ? 1 : 0);

    if (currentLength <= halfLength) {
      splitIndex = index + 1;
    }
  });

  splitIndex = Math.min(Math.max(splitIndex, 1), words.length - 1);

  return [
    words.slice(0, splitIndex).join(" "),
    words.slice(splitIndex).join(" "),
  ];
};

const Tile = ({
  title,
  titleX,
  titleY,
  titleTilt = 0,
  clipPath,
  iconClipPath,
  leftTransform,
  rightTransform,
  topTransform,
  frameTransform,
  iconTransform,
  topPath,
  hoverColor,
  isActive,
  children,
}: TileProps) => {
  const titleLines = getTitleLines(title);

  return (
    <g
      className={`group outline-none transition-transform duration-300 ease-in-out hover:-translate-y-5 hover:cursor-pointer focus-visible:-translate-y-5 ${
      isActive ? "-translate-y-5 cursor-pointer" : ""
    }`}
      style={{ "--tile-hover": hoverColor } as React.CSSProperties}
      tabIndex={0}
      role="img"
      aria-label={title}
    >
      <g clipPath={`url(#${clipPath})`}>
        <rect
          fill="var(--tile-side)"
          transform={leftTransform}
          width="35.7957"
          height="38.6628"
        />
        <rect
          fill="var(--tile-side)"
          transform={rightTransform}
          width="35.7957"
          height="38.6628"
        />
        <path
          fill="var(--tile-top)"
          className={`group-hover:fill-[var(--tile-hover)] ${
            isActive ? "fill-[var(--tile-hover)]" : ""
          }`}
          d={topPath}
          clipRule="evenodd"
          fillRule="evenodd"
        />
        <rect
          fill="url(#tile_face)"
          transform={topTransform}
          width="34.8156"
          height="34.7864"
          x="0.866025"
        />
        <rect
          stroke="var(--tile-edge)"
          transform={topTransform}
          width="34.8156"
          height="34.7864"
          x="0.866025"
        />
        <rect
          stroke="var(--tile-line)"
          strokeWidth="0.5"
          transform={frameTransform}
          rx="1.75"
          width="25.5"
          height="25.5"
          x="0.433013"
          className={`group-hover:stroke-[var(--tile-hover)] ${
            isActive ? "stroke-[var(--tile-hover)]" : ""
          }`}
        />
        <IconGlyph
          clipPath={iconClipPath}
          transform={iconTransform}
          className={`group-hover:text-[var(--tile-hover)] ${
            isActive ? "text-[var(--tile-hover)]" : ""
          }`}
        >
          {children}
        </IconGlyph>
      </g>
      <g
        className={`pointer-events-none block transition-all duration-300 ease-out sm:opacity-0 sm:group-hover:-translate-y-2 sm:group-hover:opacity-100 sm:group-focus-visible:-translate-y-2 sm:group-focus-visible:opacity-100 ${
          isActive ? "sm:-translate-y-2 sm:opacity-100" : ""
        }`}
        transform={`rotate(${titleTilt} ${titleX} ${titleY})`}
      >
        <text
          x={titleX}
          y={titleY}
          textAnchor="middle"
          dominantBaseline="middle"
          direction="rtl"
          unicodeBidi="plaintext"
          className="fill-[var(--label-text)] text-[6px] font-bold drop-shadow-[0_1px_0_rgba(255,255,255,0.9)] sm:text-[6px] dark:drop-shadow-none"
        >
          {titleLines.map((line, index) => (
            <tspan
              key={line}
              x={titleX}
              dy={index === 0 ? (titleLines.length > 1 ? "-0.35em" : "0") : "1.15em"}
            >
              {line}
            </tspan>
          ))}
        </text>
      </g>
    </g>
  );
};

export type ServiceTile = {
  readonly title: string;
  readonly href: string;
};

interface AppIconsProps {
  tiles: readonly ServiceTile[];
}

const fallbackTiles: readonly ServiceTile[] = [
  { title: "פיתוח אתרים", href: "#" },
  { title: "ניהול קמפיינים PPC", href: "#" },
  { title: "פיתוח אינטגרציות AI לעסקים", href: "#" },
  { title: "דפי נחיתה", href: "#" },
  { title: "פיתוח אפליקציות מובייל", href: "#" },
  { title: "פיתוח אפליקציות Web", href: "#" },
];

const AppIcons = ({ tiles }: AppIconsProps) => {
  const tileItems = tiles.length >= 6 ? tiles : fallbackTiles;
  const [activeTile, setActiveTile] = React.useState<number | null>(null);

  const clearActiveTile = () => {
    window.setTimeout(() => setActiveTile(null), 180);
  };

  const getTouchHoverProps = (index: number) => ({
    onPointerDown: () => setActiveTile(index),
    onPointerEnter: () => setActiveTile(index),
    onPointerLeave: () => setActiveTile(null),
    onPointerUp: clearActiveTile,
    onPointerCancel: () => setActiveTile(null),
    onBlur: () => setActiveTile(null),
  });

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 22 249 152"
      className="w-full max-w-[390px] scale-[1] overflow-visible [--label-text:#172033] [--tile-edge:#cbd5e1] [--tile-line:#64748b] [--tile-side:#d9e0ea] [--tile-top:#e8edf5] sm:w-[min(72vw,680px)] sm:max-w-none dark:[--label-text:#f8fafc] dark:[--tile-edge:#3C4149] dark:[--tile-line:#6B727C] dark:[--tile-side:#3F454E] dark:[--tile-top:#414750]"
    >
      <defs>
        <linearGradient
          id="tile_face"
          x1="0.0366998"
          y1="-0.0366699"
          x2="21.354"
          y2="42.7472"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="var(--tile-top)" />
          <stop offset="1" stopColor="var(--tile-top)" />
        </linearGradient>
        <clipPath id="clip0_67_353">
          <rect
            transform="matrix(0.866025 -0.5 0.866025 0.5 87 34.054)"
            fill="white"
            width="68.1077"
            height="67.9856"
          />
        </clipPath>
        <clipPath id="clip1_67_353">
          <rect
            transform="matrix(0.866025 -0.5 0.866025 0.5 132.074 41.6909)"
            fill="white"
            width="16"
            height="16"
          />
        </clipPath>
        <clipPath id="clip2_67_353">
          <rect
            transform="matrix(0.866025 -0.5 0.866025 0.5 44 59.054)"
            fill="white"
            width="68.1077"
            height="67.9856"
          />
        </clipPath>
        <clipPath id="clip3_67_353">
          <rect
            transform="matrix(0.866025 -0.5 0.866025 0.5 89.074 66.6909)"
            fill="white"
            width="16"
            height="16"
          />
        </clipPath>
        <clipPath id="clip4_67_353">
          <rect
            transform="matrix(0.866025 -0.5 0.866025 0.5 0 84.054)"
            fill="white"
            width="68.1077"
            height="67.9856"
          />
        </clipPath>
        <clipPath id="clip5_67_353">
          <rect
            transform="matrix(0.866025 -0.5 0.866025 0.5 45.0741 91.6909)"
            fill="white"
            width="16"
            height="16"
          />
        </clipPath>
        <clipPath id="clip6_67_353">
          <rect
            transform="matrix(0.866025 -0.5 0.866025 0.5 131 59.054)"
            fill="white"
            width="68.1077"
            height="67.9856"
          />
        </clipPath>
        <clipPath id="clip7_67_353">
          <rect
            transform="matrix(0.866025 -0.5 0.866025 0.5 176.074 66.6909)"
            fill="white"
            width="16"
            height="16"
          />
        </clipPath>
        <clipPath id="clip8_67_353">
          <rect
            transform="matrix(0.866025 -0.5 0.866025 0.5 88 84.054)"
            fill="white"
            width="68.1077"
            height="67.9856"
          />
        </clipPath>
        <clipPath id="clip9_67_353">
          <rect
            transform="matrix(0.866025 -0.5 0.866025 0.5 133.074 91.5005)"
            fill="white"
            width="16"
            height="16"
          />
        </clipPath>
        <clipPath id="clip10_67_353">
          <rect
            transform="matrix(0.866025 -0.5 0.866025 0.5 44 109.054)"
            fill="white"
            width="68.1077"
            height="67.9856"
          />
        </clipPath>
        <clipPath id="clip11_67_353">
          <rect
            transform="matrix(0.866025 -0.5 0.866025 0.5 89.074 116.691)"
            fill="white"
            width="16"
            height="16"
          />
        </clipPath>
      </defs>

      <a
        href={tileItems[0].href}
        aria-label={tileItems[0].title}
        {...getTouchHoverProps(0)}
      >
        <Tile
          title={tileItems[0].title}
          isActive={activeTile === 0}
          titleX={146}
          titleY={31}
          labelWidth={54}
          clipPath="clip0_67_353"
          iconClipPath="clip1_67_353"
          leftTransform="matrix(0.866025 0.5 0 1 114.925 41.6956)"
          rightTransform="matrix(-0.866025 0.5 0 1 176.925 41.6956)"
          topTransform="matrix(0.866025 -0.5 0.866025 0.5 115.042 42.1313)"
          frameTransform="matrix(0.866025 -0.5 0.866025 0.5 123.472 41.9074)"
          iconTransform="matrix(0.866025 -0.5 0.866025 0.5 132.074 41.6909)"
          topPath="M115.445 41.6366C115.158 41.471 114.925 41.6053 114.925 41.9366V71.6956L145.925 89.5934L176.925 71.6956V41.8582C176.925 41.6373 176.77 41.5478 176.579 41.6582L145.925 59.3561V59.2345L115.445 41.6366Z"
          hoverColor="#38bdf8"
        >
          <rect x="2.25" y="3" width="11.5" height="9.5" rx="1.4" />
          <path d="M2.25 5.75h11.5" />
          <path d="m6.1 8.1-1.8 1.65 1.8 1.65" />
          <path d="m9.9 8.1 1.8 1.65-1.8 1.65" />
          <path d="m8.7 7.6-1.4 4.3" />
        </Tile>
      </a>

      <a
        href={tileItems[1].href}
        aria-label={tileItems[1].title}
        {...getTouchHoverProps(1)}
      >
        <Tile
          title={tileItems[1].title}
          isActive={activeTile === 1}
          titleX={103}
          titleY={57}
          labelWidth={62}
          clipPath="clip2_67_353"
          iconClipPath="clip3_67_353"
          leftTransform="matrix(0.866025 0.5 0 1 71.9253 66.6956)"
          rightTransform="matrix(-0.866025 0.5 0 1 133.925 66.6956)"
          topTransform="matrix(0.866025 -0.5 0.866025 0.5 72.0419 67.1313)"
          frameTransform="matrix(0.866025 -0.5 0.866025 0.5 80.4718 66.9074)"
          iconTransform="matrix(0.866025 -0.5 0.866025 0.5 89.074 66.6909)"
          topPath="M133.579 66.6557C133.77 66.5453 133.925 66.6348 133.925 66.8557V96.6954L102.925 114.593L71.9253 96.6954V66.875C71.9253 66.6541 72.0804 66.5645 72.2717 66.675L102.925 84.3729V84.3536L133.579 66.6557Z"
          hoverColor="#f59e0b"
        >
          <circle cx="7.2" cy="8.1" r="4.3" />
          <circle cx="7.2" cy="8.1" r="1.55" />
          <path d="M10.3 5.1 13 2.4" />
          <path d="M11.1 2.3H13v1.9" />
          <path d="M2.9 13h9.7" />
        </Tile>
      </a>

      <a
        href={tileItems[2].href}
        aria-label={tileItems[2].title}
        {...getTouchHoverProps(2)}
      >
        <Tile
          title={tileItems[2].title}
          isActive={activeTile === 2}
          titleX={59}
          titleY={84}
          labelWidth={76}
          clipPath="clip4_67_353"
          iconClipPath="clip5_67_353"
          leftTransform="matrix(0.866025 0.5 0 1 27.9253 91.6956)"
          rightTransform="matrix(-0.866025 0.5 0 1 89.9253 91.6956)"
          topTransform="matrix(0.866025 -0.5 0.866025 0.5 28.0419 92.1313)"
          frameTransform="matrix(0.866025 -0.5 0.866025 0.5 36.4718 91.9074)"
          iconTransform="matrix(0.866025 -0.5 0.866025 0.5 45.0741 91.6909)"
          topPath="M89.5789 91.6584C89.7702 91.5479 89.9253 91.6375 89.9253 91.8584V121.696L58.9253 139.593L27.9253 121.695V91.8835C27.9253 91.6626 28.0804 91.5731 28.2717 91.6835L58.9253 109.381V109.356L89.5789 91.6584Z"
          hoverColor="#a78bfa"
        >
          <path d="M8 3V1.9" />
          <rect x="3" y="4" width="10" height="8" rx="2" />
          <path d="M5.25 8h.01" />
          <path d="M10.75 8h.01" />
          <path d="M6.2 10.2h3.6" />
          <path d="M1.7 7.1v2.4" />
          <path d="M14.3 7.1v2.4" />
        </Tile>
      </a>

      <a
        href={tileItems[3].href}
        aria-label={tileItems[3].title}
        {...getTouchHoverProps(3)}
      >
        <Tile
          title={tileItems[3].title}
          isActive={activeTile === 3}
          titleX={190}
          titleY={57}
          labelWidth={52}
          clipPath="clip6_67_353"
          iconClipPath="clip7_67_353"
          leftTransform="matrix(0.866025 0.5 0 1 158.925 66.6956)"
          rightTransform="matrix(-0.866025 0.5 0 1 220.925 66.6956)"
          topTransform="matrix(0.866025 -0.5 0.866025 0.5 159.042 67.1313)"
          frameTransform="matrix(0.866025 -0.5 0.866025 0.5 167.472 66.9074)"
          iconTransform="matrix(0.866025 -0.5 0.866025 0.5 176.074 66.6909)"
          topPath="M159.272 66.6889C159.08 66.5785 158.925 66.668 158.925 66.8889V96.6955L189.925 114.593L220.925 96.6956V67.7044C220.925 66.9865 220.421 66.6955 219.799 67.0544L189.925 84.3023V84.3868L159.272 66.6889Z"
          hoverColor="#60a5fa"
        >
          <rect x="2.2" y="3" width="11.6" height="10" rx="1.4" />
          <path d="M2.2 5.6h11.6" />
          <path d="M5 8h2.2v2.5H5z" />
          <path d="M9.1 8h2" />
          <path d="M9.1 10.5h2" />
        </Tile>
      </a>

      <a
        href={tileItems[4].href}
        aria-label={tileItems[4].title}
        {...getTouchHoverProps(4)}
      >
        <Tile
          title={tileItems[4].title}
          isActive={activeTile === 4}
          titleX={147}
          titleY={84}
          labelWidth={70}
          clipPath="clip8_67_353"
          iconClipPath="clip9_67_353"
          leftTransform="matrix(0.866025 0.5 0 1 115.925 91.5051)"
          rightTransform="matrix(-0.866025 0.5 0 1 177.925 91.5051)"
          topTransform="matrix(0.866025 -0.5 0.866025 0.5 116.042 91.9408)"
          frameTransform="matrix(0.866025 -0.5 0.866025 0.5 124.472 91.717)"
          iconTransform="matrix(0.866025 -0.5 0.866025 0.5 133.074 91.5005)"
          topPath="M116.272 91.4909C116.08 91.3805 115.925 91.47 115.925 91.6909V121.505L146.925 139.403V139.403L177.925 121.505V91.6441C177.925 91.4785 177.809 91.4113 177.665 91.4941L146.925 109.242V109.189L116.272 91.4909Z"
          hoverColor="#34d399"
        >
          <rect x="5" y="1.8" width="6" height="12.4" rx="1.3" />
          <path d="M7.4 12.1h1.2" />
          <path d="M6.6 4.2h2.8" />
        </Tile>
      </a>

      <a
        href={tileItems[5].href}
        aria-label={tileItems[5].title}
        {...getTouchHoverProps(5)}
      >
        <Tile
          title={tileItems[5].title}
          isActive={activeTile === 5}
          titleX={103}
          titleY={111}
          labelWidth={70}
          clipPath="clip10_67_353"
          iconClipPath="clip11_67_353"
          leftTransform="matrix(0.866025 0.5 0 1 71.9253 116.696)"
          rightTransform="matrix(-0.866025 0.5 0 1 133.925 116.696)"
          topTransform="matrix(0.866025 -0.5 0.866025 0.5 72.0419 117.131)"
          frameTransform="matrix(0.866025 -0.5 0.866025 0.5 80.4718 116.907)"
          iconTransform="matrix(0.866025 -0.5 0.866025 0.5 89.074 116.691)"
          topPath="M133.579 116.628C133.77 116.517 133.925 116.607 133.925 116.828V146.695L102.925 164.593V164.594L71.9253 146.696V116.786C71.9253 116.62 72.0416 116.553 72.1851 116.636L102.925 134.384V134.326L133.579 116.628Z"
          hoverColor="#fb7185"
        >
          <rect x="3" y="2.5" width="10" height="11" rx="1.4" />
          <path d="M5.2 5.1h5.6" />
          <path d="M5.2 7.4h5.6" />
          <path d="M5.2 10.2h3.4" />
          <path d="M10.1 10.1 13 13" />
          <path d="m10.1 10.1 1 3.6 1.9-1.9z" />
        </Tile>
      </a>
    </svg>
  );
};

export default AppIcons;
